const HTML_QUOTE_SELECTORS = [
  "blockquote",
  "div.gmail_quote",
  "div.gmail_extra",
  "div.yahoo_quoted",
  "div.zmail_extra",
  'div[id*="divRplyFwdMsg"]',
  'div[class*="OutlookMessageHeader"]',
  'div[class*="quote"]',
  'div[class*="quoted"]',
  'div[class*="original"]',
];

const PLAINTEXT_REPLY_MARKERS: RegExp[] = [
  /On\s+.+?,\s+.+?\s+(?:at\s+)?.+?\s+wrote:/i,
  /On\s+.+?\s+wrote:/i,
  /-{5,}\s*Original Message\s*-{5,}/i,
  /-{5,}\s*Forwarded message\s*-{5,}/i,
  /={5,}\s*Forwarded message\s*={5,}/i,
  /From:\s*.+?\s*Sent:\s*.+?\s*To:\s*.+?\s*Subject:/i,
  /From:\s*[^\n]+?\s+(?:To:|Date:|Subject:)/i,
  /ב-\d{1,2}\s+[^,]+,\s*\d{4}.*?כתב\/ה:/,
  /ב-\d{1,2}\s+\S+.*?כתב\/ה:/,
  /---- On .+? wrote ---/i,
];

export type SplitEmailBody = {
  /** HTML ready for sanitization and render */
  main: string;
  /** HTML ready for sanitization and render */
  quoted: string | null;
};

function stripQuotePrefixes(text: string): string {
  return text
    .split("\n")
    .map((line) => line.replace(/^\s*>\s?/, ""))
    .join("\n")
    .trim();
}

function isHtml(text: string): boolean {
  return /<[a-zA-Z][^>]*>/.test(text);
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function plainTextToDisplayHtml(text: string): string {
  return escapeHtml(text.trim()).replace(/\n/g, "<br>");
}

function findPlainTextSplitIndex(text: string): number | null {
  let earliest: number | null = null;

  for (const pattern of PLAINTEXT_REPLY_MARKERS) {
    const match = pattern.exec(text);
    if (match && (earliest === null || match.index < earliest)) {
      earliest = match.index;
    }
  }

  if (earliest !== null) {
    return earliest;
  }

  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    if (/^\s*>\s*/.test(lines[i])) {
      const offset = lines.slice(0, i).join("\n").length;
      return i > 0 ? offset + (lines[i - 1].endsWith("\n") ? 0 : 1) : 0;
    }
  }

  const inlineQuote = text.search(/\s>\s*On\s+.+\s+wrote:/i);
  if (inlineQuote > 0) {
    return inlineQuote;
  }

  return null;
}

export function splitPlainTextBody(text: string): {
  main: string;
  quoted: string | null;
} {
  const splitAt = findPlainTextSplitIndex(text);
  if (splitAt === null || splitAt <= 0) {
    return { main: text.trim(), quoted: null };
  }

  return {
    main: text.slice(0, splitAt).trim(),
    quoted: text.slice(splitAt).trim() || null,
  };
}

function splitHtmlBody(html: string): { main: string; quoted: string | null } {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const quotedNodes: string[] = [];

  for (const selector of HTML_QUOTE_SELECTORS) {
    doc.querySelectorAll(selector).forEach((node) => {
      quotedNodes.push(node.outerHTML);
      node.remove();
    });
  }

  const mainHtml = doc.body.innerHTML.trim();
  const quotedHtml = quotedNodes.join("").trim();

  if (quotedHtml) {
    return { main: mainHtml, quoted: quotedHtml };
  }

  const visibleText = doc.body.textContent ?? "";
  const plainSplit = splitPlainTextBody(visibleText);
  if (plainSplit.quoted) {
    return {
      main: plainTextToDisplayHtml(plainSplit.main),
      quoted: plainTextToDisplayHtml(stripQuotePrefixes(plainSplit.quoted)),
    };
  }

  return { main: mainHtml, quoted: null };
}

export function splitEmailBodyForDisplay(raw: string): SplitEmailBody {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { main: "", quoted: null };
  }

  if (isHtml(trimmed)) {
    const { main, quoted } = splitHtmlBody(trimmed);
    return { main, quoted };
  }

  const { main, quoted } = splitPlainTextBody(trimmed);
  return {
    main: plainTextToDisplayHtml(main),
    quoted: quoted
      ? plainTextToDisplayHtml(stripQuotePrefixes(quoted))
      : null,
  };
}
